import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../libs/axios';
import Breadcrumb from '../../utils/breadcrumbs';
import { ListLoader } from '../../utils/loader';

interface Report { id: number; content_type: string; content_id: number; reason: string | null; status: string; note: string | null; created_at: string; reporter: { id: number; name: string | null }; }

const statusStyle = (s: string) => (s === 'open' ? 'bg-yellow-100 text-yellow-800' : s === 'actioned' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800');

const Moderation: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('open');
  const [overview, setOverview] = useState<any>(null);
  const [matchId, setMatchId] = useState('');
  const [matchBusy, setMatchBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const res: any = await api.get(`/moderation/reports${statusFilter ? `?status=${statusFilter}` : ''}`); setReports(res.data || []); }
    catch (e) { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  };
  const loadOverview = async () => {
    try { const res: any = await api.get('/moderation/overview'); setOverview(res.data); }
    catch (e) { /* non-blocking */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);
  useEffect(() => { loadOverview(); }, []);

  const resolve = async (id: number, action: string) => {
    try { await api.post(`/moderation/reports/${id}/resolve`, { action }); toast.success('Resolved'); load(); }
    catch (e) { toast.error('Failed'); }
  };
  const remove = async (r: Report) => {
    try { await api.post('/moderation/remove', { content_type: r.content_type, content_id: r.content_id }); toast.success('Content removed'); }
    catch (e) { toast.error('Remove failed'); }
  };
  const ban = async (userId: number) => {
    try { await api.post('/moderation/ban-user', { user_id: userId }); toast.success('User suspended'); }
    catch (e) { toast.error('Ban failed'); }
  };
  const hideMatch = async (hidden: boolean) => {
    const id = Number(matchId.trim());
    if (!id) { toast.error('Enter a match ID'); return; }
    setMatchBusy(true);
    try {
      await api.post('/moderation/hide-match', { match_id: id, hidden });
      toast.success(hidden ? `Match #${id} hidden` : `Match #${id} unhidden`);
      setMatchId('');
      loadOverview();
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Failed'); }
    finally { setMatchBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6">
      <Breadcrumb />

      {/* Overview: who is suspended + who has hidden matches */}
      {overview && (
        <div className="mt-4 mb-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900">Suspended users</h2>
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{overview.suspended_count}</span>
            </div>
            <div className="text-sm text-gray-700 space-y-1 max-h-64 overflow-y-auto">
              {(overview.suspended || []).length === 0 ? <div className="text-gray-400">None</div> :
                overview.suspended.map((u: any) => (
                  <div key={u.user_id} className="flex items-center justify-between">
                    <span>{u.name || '(no name)'} <span className="text-gray-400">#{u.user_id}</span></span>
                    <span className="text-xs text-gray-500">{u.phone_number || '-'}</span>
                  </div>
                ))}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900">Hidden matches — by user</h2>
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">{overview.total_hidden_matches} total</span>
            </div>
            <div className="text-sm text-gray-700 space-y-1 max-h-64 overflow-y-auto">
              {(overview.hidden_by_user || []).length === 0 ? <div className="text-gray-400">None</div> :
                overview.hidden_by_user.map((u: any) => (
                  <div key={u.user_id} className="flex items-center justify-between">
                    <span>
                      {u.name || '(no name)'} <span className="text-gray-400">#{u.user_id}</span>
                      {u.status === 'suspended' && <span className="ml-2 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">suspended</span>}
                    </span>
                    <span className="text-xs font-medium text-orange-700">{u.hidden_matches} hidden</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Hide a match by id */}
      <div className="mb-6 bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-2">Hide a match by ID</h2>
        <div className="flex flex-wrap gap-2 items-center">
          <input value={matchId} onChange={(e) => setMatchId(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && hideMatch(true)}
            placeholder="Match ID" className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-40" />
          <button onClick={() => hideMatch(true)} disabled={matchBusy} className="text-sm bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg disabled:opacity-60">{matchBusy ? '…' : 'Hide match'}</button>
          <button onClick={() => hideMatch(false)} disabled={matchBusy} className="text-sm bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg disabled:opacity-60">Unhide</button>
          <span className="text-xs text-gray-400">Removes it from stats, leaderboards and listings. Reversible.</span>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Moderation</h1>
          <p className="text-sm text-gray-500">User-reported content. Remove content, suspend users, or dismiss.</p>
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
          <option value="open">Open</option><option value="actioned">Actioned</option><option value="dismissed">Dismissed</option><option value="">All</option>
        </select>
      </div>
      {loading ? <ListLoader /> : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600"><tr>
                <th className="text-left font-medium px-4 py-3">Content</th>
                <th className="text-left font-medium px-4 py-3">Reason</th>
                <th className="text-left font-medium px-4 py-3">Reporter</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-right font-medium px-4 py-3">Actions</th>
              </tr></thead>
              <tbody>
                {reports.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No reports</td></tr>
                ) : reports.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="px-4 py-3"><span className="capitalize text-gray-900">{r.content_type.replace(/_/g, ' ')}</span> <span className="text-xs text-gray-400">#{r.content_id}</span></td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{r.reason || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{r.reporter.name || `#${r.reporter.id}`}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusStyle(r.status)}`}>{r.status}</span></td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-3 flex-wrap">
                        {['feed_post', 'feed_comment', 'community', 'looking_for'].includes(r.content_type) && <button onClick={() => remove(r)} className="text-xs text-red-600 hover:underline">Remove content</button>}
                        {r.content_type === 'user' && <button onClick={() => ban(r.content_id)} className="text-xs text-red-600 hover:underline">Suspend user</button>}
                        {r.status === 'open' && (<>
                          <button onClick={() => resolve(r.id, 'action')} className="text-xs text-green-700 hover:underline">Mark actioned</button>
                          <button onClick={() => resolve(r.id, 'dismiss')} className="text-xs text-gray-500 hover:underline">Dismiss</button>
                        </>)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
export default Moderation;
